<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  filters: { type: Object, required: true },
  options: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['apply']);
const { t } = useI18n();
const input = ref('');
const mobileOpen = ref(false);
const label = option =>
  [option.street, option.number].filter(Boolean).join(', ');
const display = value =>
  [value?.address_street, value?.address_number].filter(Boolean).join(', ');

watch(
  () => props.filters,
  value => {
    input.value = display(value);
  },
  { deep: true, immediate: true }
);

const apply = () => {
  const value = input.value.trim();
  const option = props.options.find(item => label(item) === value);
  emit('apply', {
    address_street: option?.street || value,
    address_number: option?.number || '',
  });
  mobileOpen.value = false;
};

const clear = () => {
  input.value = '';
  emit('apply', { address_street: '', address_number: '' });
  mobileOpen.value = false;
};
</script>

<template>
  <div>
    <div class="relative hidden md:block">
      <label class="sr-only" for="broker-address-filter">
        {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.ADDRESS') }}
      </label>
      <span
        class="i-lucide-search pointer-events-none absolute left-3 top-3 text-lais-primary"
      />
      <input
        id="broker-address-filter"
        v-model="input"
        list="broker-address-options"
        type="search"
        autocomplete="off"
        :placeholder="
          t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.ADDRESS_PLACEHOLDER')
        "
        :disabled="loading"
        class="h-10 w-full rounded-lais border border-lais-border bg-white px-10 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
        @change="apply"
      />
      <datalist id="broker-address-options">
        <option
          v-for="option in options"
          :key="option.id"
          :value="label(option)"
        />
      </datalist>
      <button
        v-if="input"
        type="button"
        class="absolute right-2 top-2 rounded-lais px-2 py-1 text-xs font-semibold text-lais-primary"
        @click="clear"
      >
        {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.CLEAR') }}
      </button>
    </div>
    <button
      type="button"
      class="inline-flex h-10 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-sm font-semibold text-lais-text md:hidden"
      @click="mobileOpen = true"
    >
      <span class="i-lucide-search text-lais-primary" />
      {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.SEARCH') }}
    </button>
    <div
      v-if="mobileOpen"
      class="fixed inset-0 z-50 flex items-end bg-black/40 p-3 md:hidden"
      @click.self="mobileOpen = false"
    >
      <section
        class="w-full rounded-lais bg-white p-4 shadow-lais-card"
        role="dialog"
        aria-modal="true"
        :aria-label="t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.SEARCH')"
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="font-semibold text-lais-text">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.SEARCH') }}
          </h2>
          <button
            type="button"
            class="text-sm text-lais-text-muted"
            @click="mobileOpen = false"
          >
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.CLOSE') }}
          </button>
        </div>
        <input
          v-model="input"
          list="broker-address-options-mobile"
          type="search"
          class="h-10 w-full rounded-lais border border-lais-border px-3 text-sm"
          :placeholder="
            t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.ADDRESS_PLACEHOLDER')
          "
          @keyup.enter="apply"
        />
        <datalist id="broker-address-options-mobile">
          <option
            v-for="option in options"
            :key="option.id"
            :value="label(option)"
          />
        </datalist>
        <div class="mt-4 flex gap-2">
          <button
            type="button"
            class="h-10 flex-1 rounded-lais border border-lais-border text-sm font-semibold"
            @click="clear"
          >
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.CLEAR') }}
          </button>
          <button
            type="button"
            class="h-10 flex-1 rounded-lais bg-lais-primary text-sm font-semibold text-white"
            @click="apply"
          >
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.FILTERS.APPLY') }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>
